// Gabarits NEUTRES par défaut (M5, décision n° 11). Ce sont des trames, pas
// des conseils : chaque PDF d'un modèle non validé porte la mention
// `MENTION_VALIDATION`. La rédaction définitive appartient à la galerie et à
// son conseil ; Artosera ne promet rien sur leur valeur juridique.
import type { GenreContrat, Langue } from '@artosera/shared';
import type {
  ArticleContrat as IArticleContrat,
  ModeleContratPresente,
  ContratPresente,
} from './contrats.js';
import type { ContratArtistePresente } from './contratsArtistes.js';
import { CHAMPS_PAR_GENRE, GENRES_CONTRAT, LANGUES } from '@artosera/shared';

export interface ModeleContratParDefaut {
  titre: string;
  preambule: string;
  articles: IArticleContrat[];
  fait: string;
}

const FAIT = {
  fr: 'Fait à {{galerie.ville}}, le {{contrat.date}}, en deux exemplaires.',
  en: 'Signed in {{galerie.ville}} on {{contrat.date}}, in two copies.',
} as const;

export const MODELES_CONTRAT_PAR_DEFAUT: Record<
  GenreContrat,
  Record<Langue, ModeleContratParDefaut>
> = {
  nda: {
    fr: {
      titre: 'Accord de confidentialité',
      preambule:
        'Entre {{galerie.raisonSociale}}, {{galerie.adresse}}, ci-après « la Galerie », et {{contact.nom}}, {{contact.adresse}}, ci-après « le Destinataire ».',
      articles: [
        {
          titre: 'Objet',
          texte:
            'La Galerie communique au Destinataire des informations relatives à : {{saisie.objet}}. Le présent accord fixe les conditions dans lesquelles ces informations sont reçues.',
        },
        {
          titre: 'Informations confidentielles',
          texte:
            'Sont confidentielles les informations transmises par la Galerie, quel qu’en soit le support, à l’exception de celles déjà publiques au jour de leur communication.',
        },
        {
          titre: 'Engagements du Destinataire',
          texte:
            'Le Destinataire ne communique pas ces informations à un tiers et ne les utilise qu’aux fins de l’objet ci-dessus.',
        },
        {
          titre: 'Durée',
          texte: 'Le présent accord s’applique pendant {{saisie.duree}} à compter de sa signature.',
        },
        {
          titre: 'Restitution',
          texte:
            'À la demande de la Galerie, le Destinataire restitue ou détruit les documents reçus.',
        },
      ],
      fait: FAIT.fr,
    },
    en: {
      titre: 'Non-disclosure agreement',
      preambule:
        'Between {{galerie.raisonSociale}}, {{galerie.adresse}}, hereinafter “the Gallery”, and {{contact.nom}}, {{contact.adresse}}, hereinafter “the Recipient”.',
      articles: [
        {
          titre: 'Purpose',
          texte:
            'The Gallery discloses to the Recipient information relating to: {{saisie.objet}}. This agreement sets the terms on which that information is received.',
        },
        {
          titre: 'Confidential information',
          texte:
            'Any information disclosed by the Gallery, in any form, is confidential, except information already public on the day it is disclosed.',
        },
        {
          titre: 'Recipient’s undertakings',
          texte:
            'The Recipient does not disclose this information to any third party and uses it only for the purpose above.',
        },
        { titre: 'Term', texte: 'This agreement applies for {{saisie.duree}} from its signature.' },
        {
          titre: 'Return',
          texte:
            'At the Gallery’s request, the Recipient returns or destroys the documents received.',
        },
      ],
      fait: FAIT.en,
    },
  },
  depot: {
    fr: {
      titre: 'Contrat de dépôt',
      preambule:
        'Entre {{contact.nom}}, {{contact.adresse}}, ci-après « le Déposant », et {{galerie.raisonSociale}}, {{galerie.adresse}}, ci-après « la Galerie ».',
      articles: [
        {
          titre: 'Objet',
          texte:
            'Le Déposant confie à la Galerie, qui l’accepte, les œuvres suivantes :\n{{oeuvres.liste}}',
        },
        {
          titre: 'Durée',
          texte: 'Le dépôt est consenti pour {{saisie.duree}} à compter du {{contrat.date}}.',
        },
        {
          titre: 'Vente',
          texte:
            'La Galerie peut proposer les œuvres à la vente au prix convenu par écrit avec le Déposant. Sa rémunération est de {{saisie.commission}} du prix de vente hors taxes.',
        },
        {
          titre: 'Garde et assurance',
          texte:
            'La Galerie conserve les œuvres avec soin. Les conditions d’assurance sont convenues par écrit entre les parties.',
        },
        {
          titre: 'Restitution',
          texte:
            'À l’échéance, ou sur demande écrite, les œuvres non vendues sont restituées au Déposant.',
        },
      ],
      fait: FAIT.fr,
    },
    en: {
      titre: 'Consignment agreement',
      preambule:
        'Between {{contact.nom}}, {{contact.adresse}}, hereinafter “the Consignor”, and {{galerie.raisonSociale}}, {{galerie.adresse}}, hereinafter “the Gallery”.',
      articles: [
        {
          titre: 'Purpose',
          texte:
            'The Consignor entrusts the Gallery, which accepts, with the following works:\n{{oeuvres.liste}}',
        },
        {
          titre: 'Term',
          texte: 'The consignment is granted for {{saisie.duree}} from {{contrat.date}}.',
        },
        {
          titre: 'Sale',
          texte:
            'The Gallery may offer the works for sale at the price agreed in writing with the Consignor. Its fee is {{saisie.commission}} of the sale price before tax.',
        },
        {
          titre: 'Custody and insurance',
          texte:
            'The Gallery keeps the works with care. Insurance terms are agreed in writing between the parties.',
        },
        {
          titre: 'Return',
          texte:
            'At the end of the term, or upon written request, unsold works are returned to the Consignor.',
        },
      ],
      fait: FAIT.en,
    },
  },
  artiste: {
    fr: {
      titre: 'Contrat de représentation',
      preambule:
        'Entre {{artiste.nom}}, artiste ({{artiste.nationalite}}), ci-après « l’Artiste », et {{galerie.raisonSociale}}, {{galerie.adresse}}, ci-après « la Galerie ».',
      articles: [
        {
          titre: 'Objet',
          texte:
            'L’Artiste confie à la Galerie la représentation de son travail sur le territoire suivant : {{saisie.territoire}}.',
        },
        {
          titre: 'Durée',
          texte: 'Le contrat prend effet le {{contrat.date}} pour {{saisie.duree}}.',
        },
        { titre: 'Exclusivité', texte: 'Exclusivité : {{saisie.exclusivite}}.' },
        {
          titre: 'Rémunération',
          texte:
            'Sur chaque vente, la Galerie perçoit {{saisie.commission}} du prix de vente hors taxes ; le solde revient à l’Artiste.',
        },
        {
          titre: 'Engagements de la Galerie',
          texte: 'La Galerie promeut le travail de l’Artiste et lui rend compte des ventes.',
        },
        {
          titre: 'Fin du contrat',
          texte:
            'Chaque partie peut mettre fin au contrat par écrit, dans les conditions convenues entre elles.',
        },
      ],
      fait: FAIT.fr,
    },
    en: {
      titre: 'Representation agreement',
      preambule:
        'Between {{artiste.nom}}, artist ({{artiste.nationalite}}), hereinafter “the Artist”, and {{galerie.raisonSociale}}, {{galerie.adresse}}, hereinafter “the Gallery”.',
      articles: [
        {
          titre: 'Purpose',
          texte:
            'The Artist entrusts the Gallery with the representation of their work in the following territory: {{saisie.territoire}}.',
        },
        {
          titre: 'Term',
          texte: 'The agreement takes effect on {{contrat.date}} for {{saisie.duree}}.',
        },
        { titre: 'Exclusivity', texte: 'Exclusivity: {{saisie.exclusivite}}.' },
        {
          titre: 'Remuneration',
          texte:
            'On each sale, the Gallery receives {{saisie.commission}} of the sale price before tax; the balance goes to the Artist.',
        },
        {
          titre: 'Gallery’s undertakings',
          texte: 'The Gallery promotes the Artist’s work and reports sales to the Artist.',
        },
        {
          titre: 'Termination',
          texte:
            'Either party may terminate the agreement in writing, on the terms agreed between them.',
        },
      ],
      fait: FAIT.en,
    },
  },
};

const jour = (n: number) => new Date(Date.now() + n * 86400000).toISOString();
export const DEMO_MODELES_CONTRATS: ModeleContratPresente[] = GENRES_CONTRAT.flatMap((genre) =>
  LANGUES.map((langue) => ({
    genre,
    langue,
    ...MODELES_CONTRAT_PAR_DEFAUT[genre][langue],
    version: 1,
    valideParGalerie: false,
    champs: CHAMPS_PAR_GENRE[genre],
    modifieLe: jour(-4),
  })),
);
export const DEMO_CONTRATS: ContratPresente[] = [
  {
    id: 'contrat-1',
    numero: 'CT-2026-0001',
    genre: 'nda',
    langue: 'fr',
    titre: 'Accord de confidentialité',
    etat: 'signe',
    artisteId: null,
    contactId: 'contact-1',
    oeuvres: [],
    emisLe: jour(-10),
    signeLe: jour(-8),
    annuleLe: null,
    mentionValidation: true,
    modeleVersion: 1,
    documentId: null,
    preambule: 'Entre Galerie Sainte-Avoye et Étienne Lenoir.',
    articles: [{ titre: 'Objet', texte: 'Présentation privée d’une collection.' }],
    fait: 'Fait à Paris.',
  },
  {
    id: 'contrat-2',
    numero: 'CT-2026-0002',
    genre: 'artiste',
    langue: 'fr',
    titre: 'Contrat de représentation',
    etat: 'genere',
    artisteId: 'artiste-1',
    contactId: null,
    oeuvres: [],
    emisLe: jour(-3),
    signeLe: null,
    annuleLe: null,
    mentionValidation: true,
    modeleVersion: 1,
    documentId: null,
    preambule: 'Entre Léa Bertrand et Galerie Sainte-Avoye.',
    articles: [{ titre: 'Objet', texte: 'Représentation du travail de l’artiste.' }],
    fait: 'Fait à Paris.',
  },
];
export const DEMO_CONTRATS_ARTISTES: ContratArtistePresente[] = [25, -5, 400].map((n, i) => ({
  id: `contrat-artiste-${i + 1}`,
  artisteId: `artiste-${i + 1}`,
  debutLe: jour(-700),
  finLe: jour(n),
  exclusivite: true,
  commission: '50 %',
  territoire: 'Europe',
  etat: i === 2 ? 'brouillon' : 'en_vigueur',
  echu: n < 0,
  joursAvantEcheance: n,
  resilieLe: null,
  motifResiliation: '',
  rappelsJours: [90, 30, 7],
  contratGenereId: i === 0 ? 'contrat-2' : null,
  documentId: null,
  note: '',
  creeLe: jour(-700),
  modifieLe: jour(-5),
}));
